import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users,
  UserPlus,
  Shield,
  Mail,
  Building2,
  MoreVertical,
  CheckCircle2,
  XCircle,
  Search,
  Filter,
  Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { api } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const ROLES = [
  { value: 'admin', label: 'Admin', color: 'bg-red-100 text-red-700' },
  { value: 'project_manager', label: 'Project Manager', color: 'bg-purple-100 text-purple-700' },
  { value: 'content_account', label: 'Content Account', color: 'bg-amber-100 text-amber-700' },
  { value: 'creative_account', label: 'Creative Account', color: 'bg-pink-100 text-pink-700' },
  { value: 'execution_team', label: 'Execution Team', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'client', label: 'Client', color: 'bg-blue-100 text-blue-700' },
];

const getRoleBadge = (role) => {
  const roleConfig = ROLES.find(r => r.value === role) || ROLES[6];
  return roleConfig;
};

const getApiErrorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg || item.message || String(item)).join(', ');
  }
  if (detail && typeof detail === 'object') {
    return detail.message || detail.msg || fallback;
  }
  return detail || fallback;
};

const UserRow = ({ user, onRoleChange, onStatusChange, onVerifyEmail, onRequestDelete, currentUserId }) => {
  const roleConfig = getRoleBadge(user.role);
  const isCurrentUser = user.user_id === currentUserId;

  return (
    <motion.tr
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50"
    >
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <span className="font-semibold text-indigo-600">{user.name?.charAt(0)?.toUpperCase() || 'U'}</span>
          </div>
          <div>
            <p className="font-medium">{user.name}</p>
            <p className="text-sm text-slate-500">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="py-4 px-4">
        <Badge className={roleConfig.color}>
          {roleConfig.label}
        </Badge>
      </td>
      <td className="py-4 px-4">
        <span className="text-sm text-slate-600">{user.company_name || '-'}</span>
      </td>
      <td className="py-4 px-4">
        <div className="flex flex-col gap-1">
          <Badge variant={user.is_active ? 'success' : 'secondary'}>
            {user.is_active ? 'Active' : 'Inactive'}
          </Badge>
          {user.role === 'client' && (
            <Badge variant={user.email_verified ? 'success' : 'secondary'} className="text-xs">
              {user.email_verified ? 'Email verified' : 'Email unverified'}
            </Badge>
          )}
        </div>
      </td>
      <td className="py-4 px-4">
        <span className="text-sm text-slate-500">
          {new Date(user.created_at).toLocaleDateString()}
        </span>
      </td>
      <td className="py-4 px-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" disabled={isCurrentUser}>
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onRoleChange(user)}>
              <Shield className="w-4 h-4 mr-2" />
              Change Role
            </DropdownMenuItem>
            {user.role === 'client' && !user.email_verified && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-blue-600"
                  onClick={() => onVerifyEmail(user.user_id)}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Verify Email
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            {user.is_active ? (
              <DropdownMenuItem
                className="text-red-600"
                onClick={() => onStatusChange(user.user_id, 'deactivate')}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Deactivate
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                className="text-green-600"
                onClick={() => onStatusChange(user.user_id, 'activate')}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Activate
              </DropdownMenuItem>
            )}
            {!user.is_active && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600"
                  onClick={() => onRequestDelete(user)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete account
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </motion.tr>
  );
};

const CreateUserForm = ({ onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'project_manager',
    company_name: 'GDS Internal'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (_) {
      // Parent handler shows the API error toast; keep the dialog open for correction.
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Full Name</Label>
        <Input
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="John Doe"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="john@company.com"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          placeholder="••••••••"
          required
          minLength={8}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="role">Role</Label>
        <Select
          value={formData.role}
          onValueChange={(value) => setFormData({ ...formData, role: value })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select role" />
          </SelectTrigger>
          <SelectContent>
            {ROLES.filter(r => r.value !== 'client').map((role) => (
              <SelectItem key={role.value} value={role.value}>
                {role.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-500">
          Note: Clients register themselves. Use this form for internal team members only.
        </p>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" className="gds-btn-primary" disabled={loading}>
          {loading ? 'Creating...' : 'Create User'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const ChangeRoleDialog = ({ user, onSubmit, onClose }) => {
  const [newRole, setNewRole] = useState(user?.role || 'client');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit(user.user_id, newRole);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Change role for <strong>{user?.name}</strong> ({user?.email})
      </p>
      
      <div className="space-y-2">
        <Label>New Role</Label>
        <Select value={newRole} onValueChange={setNewRole}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROLES.map((role) => (
              <SelectItem key={role.value} value={role.value}>
                {role.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button className="gds-btn-primary" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Updating...' : 'Update Role'}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [roleChangeUser, setRoleChangeUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      const data = response.data;
      setUsers(Array.isArray(data) ? data : (data?.items || []));
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (formData) => {
    try {
      await api.post('/admin/create-user', formData);
      toast.success('User created successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(getApiErrorMessage(error, 'Failed to create user'));
      throw error;
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { new_role: newRole });
      toast.success('Role updated successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error changing role:', error);
      toast.error('Failed to update role');
    }
  };

  const handleVerifyEmail = async (userId) => {
    try {
      await api.patch(`/admin/users/${userId}/verify-email`);
      toast.success('Email marked as verified');
      fetchUsers();
    } catch (error) {
      console.error('Error verifying email:', error);
      toast.error('Failed to verify email');
    }
  };

  const handleStatusChange = async (userId, action) => {
    try {
      await api.patch(`/admin/users/${userId}/${action}`);
      toast.success(`User ${action}d successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Error changing status:', error);
      toast.error('Failed to update user status');
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete?.user_id) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${userToDelete.user_id}`);
      toast.success('Account removed permanently');
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(getApiErrorMessage(error, 'Failed to delete user'));
    } finally {
      setDeleting(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const stats = {
    total: users.length,
    active: users.filter(u => u.is_active).length,
    clients: users.filter(u => u.role === 'client').length,
    internal: users.filter(u => u.role !== 'client').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">User Management</h1>
          <p className="text-slate-600 dark:text-slate-400">
            Manage team members and client accounts
          </p>
        </div>
        <Button className="gds-btn-primary" onClick={() => setShowCreateDialog(true)}>
          <UserPlus className="w-4 h-4 mr-2" />
          Add Team Member
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="gds-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-slate-500">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="gds-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active}</p>
                <p className="text-sm text-slate-500">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="gds-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.clients}</p>
                <p className="text-sm text-slate-500">Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="gds-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.internal}</p>
                <p className="text-sm text-slate-500">Internal Team</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="gds-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {ROLES.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="gds-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                  <th className="text-left py-3 px-4 font-medium text-sm text-slate-600">User</th>
                  <th className="text-left py-3 px-4 font-medium text-sm text-slate-600">Role</th>
                  <th className="text-left py-3 px-4 font-medium text-sm text-slate-600">Company</th>
                  <th className="text-left py-3 px-4 font-medium text-sm text-slate-600">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-sm text-slate-600">Created</th>
                  <th className="text-left py-3 px-4 font-medium text-sm text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No users found</p>
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <UserRow
                      key={user.user_id}
                      user={user}
                      currentUserId={currentUser?.user_id}
                      onRoleChange={(user) => setRoleChangeUser(user)}
                      onStatusChange={handleStatusChange}
                      onVerifyEmail={handleVerifyEmail}
                      onRequestDelete={setUserToDelete}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
          </DialogHeader>
          <CreateUserForm
            onSubmit={handleCreateUser}
            onClose={() => setShowCreateDialog(false)}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!userToDelete}
        onOpenChange={(open) => {
          if (!open) setUserToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this account?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong>{userToDelete?.name}</strong> ({userToDelete?.email}).
              Only inactive accounts can be deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              onClick={confirmDeleteUser}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete account'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Role Dialog */}
      <Dialog open={!!roleChangeUser} onOpenChange={() => setRoleChangeUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
          </DialogHeader>
          {roleChangeUser && (
            <ChangeRoleDialog
              user={roleChangeUser}
              onSubmit={handleRoleChange}
              onClose={() => setRoleChangeUser(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
